const chapter = {
  id: 'properties-of-matter',
  slug: 'properties-of-matter',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Mechanical Properties of Solids & Fluids',
        hindi: 'ठोस और तरल के यांत्रिक गुण',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'elasticity',
      title: {
        english: 'Elasticity — Stress, Strain, Moduli',
        hindi: 'प्रत्यास्थता — प्रतिबल, विकृति, प्रत्यास्थता गुणांक',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Stress & strain',
        hindi: 'तनाव और दबाव',
          },
          body: {
            english: '**Stress** = restoring force per unit area, $\\sigma = F/A$ (Pa). **Strain** = fractional deformation (dimensionless). Types: longitudinal, shear, volumetric.',
        hindi: '**तनाव** = प्रति इकाई क्षेत्र पुनर्स्थापन बल, $\\sigma = F/A$ (Pa)। **तनाव** = आंशिक विरूपण (आयाम रहित)। प्रकार: अनुदैर्ध्य, कतरनी, वॉल्यूमेट्रिक।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Hooke\'s law',
        hindi: 'हुक का नियम',
          },
          statement: {
            english: 'Within elastic limit, stress $\\propto$ strain. The proportionality constant is the **modulus of elasticity**.',
        hindi: 'लोचदार सीमा के भीतर, तनाव $\\propto$ तनाव। आनुपातिकता स्थिरांक **लोच का मापांक** है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Young\'s modulus** $Y = \\dfrac{F/A}{\\Delta L/L} = \\dfrac{FL}{A\\Delta L}$ (longitudinal).',
        hindi: '**यंग का मापांक** $Y = \\dfrac{F/A}{\\Delta L/L} = \\dfrac{FL}{A\\Delta L}$ (अनुदैर्ध्य)।',
            },
            {
              english: '**Bulk modulus** $K = -\\dfrac{\\Delta P}{\\Delta V/V}$ (volumetric). **Compressibility** $= 1/K$.',
        hindi: '**बल्क मापांक** $K = -\\dfrac{\\Delta P}{\\Delta V/V}$ (वॉल्यूमेट्रिक)। **संपीड़न** $= 1/K$.',
            },
            {
              english: '**Shear modulus** (rigidity) $\\eta = \\dfrac{F/A}{\\Delta x/L} = \\dfrac{F/A}{\\theta}$.',
        hindi: '**कतरनी मापांक** (कठोरता) $\\eta = \\dfrac{F/A}{\\Delta x/L} = \\dfrac{F/A}{\\theta}$।',
            },
            {
              english: '**Poisson\'s ratio** $\\sigma = -\\dfrac{\\text{lateral strain}}{\\text{longitudinal strain}}$. Theoretical range $-1 \\le \\sigma \\le 0.5$; typical $0.2$–$0.4$.',
        hindi: '**पॉइसन का अनुपात** $\\sigma = -\\dfrac{\\text{पार्श्व तनाव}}{\\text{अनुदैर्ध्य तनाव}}$। सैद्धांतिक सीमा $-1 \\le \\sigma \\le 0.5$; सामान्य $0.2$–$0.4$.',
            },
            {
              english: '**Elastic PE per unit volume** $u = \\tfrac12 \\times \\text{stress} \\times \\text{strain} = \\tfrac{\\sigma^2}{2Y}$.',
        hindi: '**प्रति इकाई आयतन लोचदार पीई** $u = \\tfrac12 \\times \\text{stress} \\times \\text{strain} = \\tfrac{\\sigma^2}{2Y}$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A steel wire ($Y = 2 \\times 10^{11}$ Pa) of length 2 m and cross-section $1\\ \\text{mm}^2$ stretches by 1 mm under load $F$. Find $F$.',
        hindi: 'एक स्टील का तार ($Y = 2 \\times 10^{11}$ Pa) जिसकी लंबाई 2 मीटर और क्रॉस-सेक्शन $1\\\\ \\text{mm}^2$ है, भार $F$ के तहत 1 मिमी तक फैला हुआ है। $F$ खोजें।',
          },
          solution: {
            english: '$F = YA\\,\\Delta L/L = 2{\\times}10^{11} \\cdot 10^{-6} \\cdot 10^{-3}/2 = 100$ N.',
        hindi: '$F = YA\\,\\Delta L/L = 2{\\times}10^{11} \\cdot 10^{-6} \\cdot 10^{-3}/2 = 100$ N.',
          },
        },
      ],
    },
    {
      id: 'pressure',
      title: {
        english: 'Fluid Statics — Pressure & Pascal\'s Law',
        hindi: 'द्रव स्थैतिकी — दाब और पास्कल का नियम',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Pressure $P = F/A$ (Pa). **Atmospheric** $\\approx 1.013\\times 10^5$ Pa $= 760$ mmHg.',
        hindi: 'दबाव $P = F/A$ (पा). **वायुमंडलीय** $\\approx 1.013\\times 10^5$ Pa $= 760$ mmHg.',
            },
            {
              english: 'Pressure at depth $h$ in fluid of density $\\rho$: $P = P_0 + \\rho g h$.',
        hindi: '$\\rho$ घनत्व वाले द्रव में $h$ गहराई पर दबाव: $P = P_0 + \\rho g h$।',
            },
            {
              english: '**Pascal\'s law**: pressure applied to an enclosed incompressible fluid is transmitted equally to all parts. Basis of **hydraulic lift**: $F_1/A_1 = F_2/A_2$.',
        hindi: '**पास्कल का नियम**: एक बंद असंपीड्य द्रव पर लगाया गया दबाव सभी भागों में समान रूप से प्रसारित होता है। **हाइड्रोलिक लिफ्ट** का आधार: $F_1/A_1 = F_2/A_2$।',
            },
            {
              english: '**Archimedes\' principle**: Buoyant force = weight of fluid displaced $= \\rho_{\\text{fluid}} V_{\\text{sub}} g$. Floating: $\\rho_{\\text{body}} V = \\rho_{\\text{fluid}} V_{\\text{sub}}$.',
        hindi: '**आर्किमिडीज़ का सिद्धांत**: उत्प्लावन बल = विस्थापित द्रव का भार $= \\rho_{\\text{fluid}} V_{\\text{sub}} g$। फ़्लोटिंग: $\\rho_{\\text{body}} V = \\rho_{\\text{fluid}} V_{\\text{sub}}$.',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Fraction of an iceberg ($\\rho = 920$ kg/m³) submerged in seawater ($\\rho = 1030$).',
        hindi: 'एक हिमखंड का अंश ($\\rho = 920$ kg/m³) समुद्री जल में डूबा हुआ ($\\rho = 1030$)।',
          },
          solution: {
            english: 'Floating: $V_{\\text{sub}}/V = \\rho_{\\text{ice}}/\\rho_{\\text{sea}} = 920/1030 \\approx 0.893$ → about 89.3%.',
        hindi: 'फ़्लोटिंग: $V_{\\text{sub}}/V = \\rho_{\\text{ice}}/\\rho_{\\text{sea}} = 920/1030 \\approx 0.893$ → लगभग 89.3%।',
          },
        },
      ],
    },
    {
      id: 'flow',
      title: {
        english: 'Fluid Dynamics — Continuity & Bernoulli',
        hindi: 'द्रव गतिकी — सातत्य और बर्नूली',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Equation of continuity',
        hindi: 'निरंतरता का समीकरण',
          },
          statement: {
            english: 'For incompressible steady flow: $A_1 v_1 = A_2 v_2$ (volume flow rate const).',
        hindi: 'असम्पीडित स्थिर प्रवाह के लिए: $A_1 v_1 = A_2 v_2$ (आयतन प्रवाह दर स्थिरांक)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Bernoulli\'s equation',
        hindi: 'बर्नौली का समीकरण **टोरिसेली का नियम** (गहराई पर छोटे छेद से प्रवाह): ।',
          },
          statement: {
            english: `Along a streamline of an ideal incompressible fluid:
$$P + \\tfrac12 \\rho v^2 + \\rho g h = \\text{constant}.$$
Derived from work-energy theorem applied to fluid element.`,
            hindi: 'एक आदर्श असंपीड्य द्रव की धारारेखा के साथ: $$P + \\tfrac12 \\rho v^2 + \\rho g h = \\text{constant}.$$ द्रव तत्व पर लागू कार्य-ऊर्जा प्रमेय से व्युत्पन्न।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Torricelli\'s law** (efflux from small hole at depth $h$): $v = \\sqrt{2gh}$.',
        hindi: '**टोरिसेली का नियम** ($h$ गहराई पर छोटे छेद से प्रवाह): $v = \\sqrt{2gh}$।',
            },
            {
              english: '**Venturi meter**, lift on aerofoil, atomizers — all use lower pressure with higher speed.',
        hindi: '**वेंचुरी मीटर**, एयरोफ़ॉइल पर लिफ्ट, एटमाइज़र - सभी उच्च गति के साथ कम दबाव का उपयोग करते हैं। **रेनॉल्ड्स नंबर** । यदि है तो प्रवाह लैमिनायर है, यदि है तो अशांत है।',
            },
            {
              english: '**Reynolds number** $Re = \\rho v d / \\eta$. Flow laminar if $Re < 1000$, turbulent if $Re > 2000$.',
        hindi: '**रेनॉल्ड्स नंबर** $Re = \\rho v d / \\eta$। यदि $Re < 1000$ है तो प्रवाह लैमिनायर है, यदि $Re > 2000$ है तो अशांत है।',
            },
          ],
        },
      ],
    },
    {
      id: 'viscosity',
      title: {
        english: 'Viscosity & Stokes\' Law',
        hindi: 'श्यानता और स्टोक्स का नियम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Viscosity',
        hindi: 'चिपचिपाहट',
          },
          body: {
            english: 'Tangential force per unit area between two layers of fluid in relative motion: $F/A = \\eta\\,dv/dy$. SI unit: Pa·s. Coefficient $\\eta$.',
        hindi: 'सापेक्ष गति में द्रव की दो परतों के बीच प्रति इकाई क्षेत्र स्पर्शरेखा बल: $F/A = \\eta\\,dv/dy$। एसआई इकाई: Pa·s. गुणांक $\\eta$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Stokes\' law',
        hindi: 'स्टोक्स का नियम',
          },
          statement: {
            english: 'Drag on a small sphere of radius $r$ moving at speed $v$ in fluid: $F = 6\\pi\\eta r v$.',
        hindi: 'द्रव में $r$ गति $v$ से चलते हुए त्रिज्या $F = 6\\pi\\eta r v$ के एक छोटे गोले पर खींचें।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Terminal velocity',
        hindi: 'टर्मिनल वेग',
          },
          statement: {
            english: `Sphere of radius $r$, density $\\rho$, in fluid of density $\\sigma$ and viscosity $\\eta$: equating net weight to drag,
$$v_T = \\dfrac{2 r^2 (\\rho - \\sigma) g}{9 \\eta}.$$`,
            hindi: 'त्रिज्या $r$, घनत्व $\\rho$ का गोला, घनत्व $\\sigma$ और श्यानता $\\eta$ के द्रव में: खींचने के लिए शुद्ध वजन के बराबर, $$v_T = \\dfrac{2 r^2 (\\rho - \\sigma) g}{9 \\eta}.$$ त्रिज्या $r$ की एक केशिका ट्यूब में सतह तनाव $\\rho$, घनत्व $\\sigma$, संपर्क कोण $\\eta$ के तरल में डुबोया गया: $$v_T = \\dfrac{2 r^2 (\\rho - \\sigma) g}{9 \\eta}.$$ यदि , (अवसाद, उदाहरणार्थ पारा)।',
          },
        },
      ],
    },
    {
      id: 'surface-tension',
      title: {
        english: 'Surface Tension & Capillarity',
        hindi: 'पृष्ठ तनाव और केशिकत्व',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Surface tension',
        hindi: 'सतही तनाव',
          },
          body: {
            english: 'Force per unit length acting tangentially on the surface of a liquid: $T = F/L$ (N/m). Equivalent: surface energy per unit area (J/m²).',
        hindi: 'तरल की सतह पर स्पर्शरेखीय रूप से कार्य करने वाला प्रति इकाई लंबाई बल: $T = F/L$ (N/m)। समतुल्य: प्रति इकाई क्षेत्र सतह ऊर्जा (J/m²)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Excess pressure inside a **liquid drop**: $P_{\\text{excess}} = 2T/R$.',
        hindi: '**तरल बूंद** के अंदर अतिरिक्त दबाव: $P_{\\text{excess}} = 2T/R$।',
            },
            {
              english: 'Inside a **soap bubble** (two surfaces): $P_{\\text{excess}} = 4T/R$.',
        hindi: 'एक **साबुन के बुलबुले** के अंदर (दो सतहें): $P_{\\text{excess}} = 4T/R$।',
            },
            {
              english: 'Inside an **air bubble** in liquid: $2T/R$.',
        hindi: 'तरल में एक **हवा के बुलबुले** के अंदर: $2T/R$। **संपर्क का कोण** $2T/R$: पानी-ग्लास (गीला करना), पारा-ग्लास (गैर-गीला करना)।',
            },
            {
              english: '**Angle of contact** $\\theta$: water-glass $\\approx 0°$ (wetting), mercury-glass $\\approx 140°$ (non-wetting).',
        hindi: '**संपर्क का कोण** $\\theta$: पानी-ग्लास $\\approx 0°$ (गीला करना), पारा-ग्लास $\\approx 140°$ (गीला न करना)।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Capillary rise (Jurin\'s law)',
        hindi: 'केशिका वृद्धि (ज्यूरिन का नियम)',
          },
          statement: {
            english: `In a capillary tube of radius $r$ dipped in a liquid of surface tension $T$, density $\\rho$, contact angle $\\theta$:
$$h = \\dfrac{2 T \\cos\\theta}{r \\rho g}.$$
If $\\theta > 90°$, $h < 0$ (depression, e.g. mercury).`,
            hindi: 'त्रिज्या $r$ की एक केशिका ट्यूब में सतह तनाव $T$, घनत्व $\\rho$, संपर्क कोण $\\theta$ के तरल में डुबोया गया: $$h = \\dfrac{2 T \\cos\\theta}{r \\rho g}.$$ यदि $\\theta > 90°$, $h < 0$ (अवसाद, उदाहरणार्थ पारा)।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Water rises 10 cm in a capillary of radius 0.2 mm. Surface tension? ($\\theta = 0$, $\\rho = 1000$, $g = 10$)',
        hindi: '0.2 मिमी त्रिज्या की केशिका में पानी 10 सेमी ऊपर उठता है। सतही तनाव? ($\\theta = 0$, $\\rho = 1000$, $g = 10$)',
          },
          solution: {
            english: '$T = h r \\rho g / 2 = 0.1 \\cdot 2{\\times}10^{-4} \\cdot 1000 \\cdot 10 / 2 = 0.1$ N/m.',
        hindi: '$T = h r \\rho g / 2 = 0.1 \\cdot 2{\\times}10^{-4} \\cdot 1000 \\cdot 10 / 2 = 0.1$ N/m.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'Y = FL/(A\\,\\Delta L)',
      desc: {
        english: 'Young\'s modulus',
        hindi: 'यंग का मापांक',
      },
    },
    {
      latex: 'P = P_0 + \\rho g h',
      desc: {
        english: 'Hydrostatic pressure',
        hindi: 'हाइड्रोस्टेटिक दबाव',
      },
    },
    {
      latex: 'F_b = \\rho_f V_{\\text{sub}} g',
      desc: {
        english: 'Buoyancy',
        hindi: 'उछाल',
      },
    },
    {
      latex: 'A_1 v_1 = A_2 v_2',
      desc: {
        english: 'Continuity',
        hindi: 'निरंतरता',
      },
    },
    {
      latex: 'P + \\tfrac12\\rho v^2 + \\rho g h = \\text{const}',
      desc: {
        english: 'Bernoulli',
        hindi: 'बर्नौली',
      },
    },
    {
      latex: 'v_T = 2 r^2 (\\rho-\\sigma) g/(9\\eta)',
      desc: {
        english: 'Terminal velocity',
        hindi: 'टर्मिनल वेग',
      },
    },
    {
      latex: 'h = 2T\\cos\\theta/(r\\rho g)',
      desc: {
        english: 'Capillary rise',
        hindi: 'केशिका वृद्धि',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Elasticity',
      problem: {
        english: 'A wire of $Y = 2{\\times}10^{11}$ Pa, area $10^{-6}\\ \\text{m}^2$, length 1 m stretches 0.5 mm. Force?',
        hindi: '$Y = 2{\\times}10^{11}$ Pa, क्षेत्रफल $10^{-6}\\\\ \\text{m}^2$, लंबाई 1 मीटर का एक तार 0.5 मिमी तक फैला है। बल?',
      },
      answer: {
        english: '$100$ N',
        hindi: '$100$ एन',
      },
      solution: {
        english: '$F = YA\\Delta L/L$.',
        hindi: '$F = YA\\Delta L/L$।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Bernoulli',
      problem: {
        english: 'Speed of efflux from a hole 5 m below water surface in an open tank.',
        hindi: 'एक खुले टैंक में पानी की सतह से 5 मीटर नीचे एक छेद से प्रवाह की गति।',
      },
      answer: {
        english: '$10$ m/s',
        hindi: '$10$ m/s',
      },
      solution: {
        english: '$v = \\sqrt{2gh}$.',
        hindi: '$v = \\sqrt{2gh}$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Surface tension',
      problem: {
        english: 'Excess pressure inside a soap bubble of radius 1 cm with $T = 0.04$ N/m.',
        hindi: '$T = 0.04$ N/m के साथ 1 सेमी त्रिज्या के साबुन के बुलबुले के अंदर अतिरिक्त दबाव।',
      },
      answer: {
        english: '$16$ Pa',
        hindi: '$16$ पा',
      },
      solution: {
        english: '$\\Delta P = 4T/R = 4(0.04)/0.01 = 16$ Pa.',
        hindi: '$\\Delta P = 4T/R = 4(0.04)/0.01 = 16$ पा.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Stokes',
      problem: {
        english: 'Terminal velocity of a 1 mm radius water droplet in air ($\\eta = 1.8{\\times}10^{-5}$). Take $\\rho_{\\text{water}} - \\rho_{\\text{air}} \\approx 10^3$.',
        hindi: 'हवा में 1 मिमी त्रिज्या वाली पानी की बूंद का टर्मिनल वेग ($\\eta = 1.8{\\times}10^{-5}$)। $\\rho_{\\text{water}} - \\rho_{\\text{air}} \\approx 10^3$ लें।',
      },
      answer: {
        english: '$\\approx 121$ m/s (theoretical)',
        hindi: '$\\approx 121$ मी/से (सैद्धांतिक)',
      },
      solution: {
        english: '$v_T = 2(10^{-3})^2 (10^3)(10)/(9 \\cdot 1.8{\\times}10^{-5})$.',
        hindi: '$v_T = 2(10^{-3})^2 (10^3)(10)/(9 \\cdot 1.8{\\times}10^{-5})$.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Buoyancy',
      problem: {
        english: 'A block floats with 3/4 submerged in water. Density of block?',
        hindi: 'एक ब्लॉक पानी में डूबे हुए 3/4 पर तैरता है। ब्लॉक का घनत्व?',
      },
      answer: {
        english: '$750$ kg/m³',
        hindi: '$750$ kg/m³',
      },
      solution: {
        english: '$\\rho_b = (3/4) \\rho_w$.',
        hindi: '$\\rho_b = (3/4) \\rho_w$.',
      },
    },
  ],
  next: {
    slug: 'thermodynamics',
    title: 'Thermodynamics & Kinetic Theory',
  },
};

export default chapter;
